import React, {
  useState,
  useMemo,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { DEFAULT_THEME_COLOR } from 'appenv';
import PopupButton from 'components/PopupButton';
import ColoredMaterialIcon, { ColoredMaterialIconProps } from 'components/twilio/components/styled/ColoredMaterialIcon';
import { MoreHoriz } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import ReactHtmlParser from 'react-html-parser';

import { selectLocale } from 'models/localization';
import { Booth, DesignElement, DesignElementKind } from 'models/cms/booths';
import MobileProfileModal from 'components/booth/MobileProfileModal';
import MobileBoothBannerSlider from './MobileSlider';

const FlexContainer = styled.div`
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding-bottom: 20px;
  position: relative;
`;

const LogoContainer = styled.div`
  width: 100%;
  margin: 16px auto;
  text-align: center;
`;

const NameText = styled.div`
  font-size: 18px;
  line-height: 18px;
  margin: 0 16px 8px;
  text-align: center;
`;

const HeadLine = styled(NameText)`
  font-size: 16px;
  color: ${DEFAULT_THEME_COLOR};
  text-align: center;

  & * {
    line-height: 1em;
  }
`;

const OverflowBlocker = styled.div`
  width: 17px;
  background-color: #FEFEFE;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
`;

const OverflowBlockerRight = styled(OverflowBlocker)`
  right: 0;
  left: auto;
`;

const isDesignElementSupported = (asset: DesignElement, locale: string) => {
  if (!asset) return false;
  switch (asset.kind) {
    case DesignElementKind.Image: return !!asset.image[locale]?.originalImageUrl;
    case DesignElementKind.PDF: return !!(asset.pdf[locale]?.fileUrl || asset.thumbnailImage[locale]?.originalImageUrl);
    case DesignElementKind.Video: return !!asset.video[locale]?.playbackUrl;
    case DesignElementKind.Richtext:
    default: return false;
  }
};

const iconProps: ColoredMaterialIconProps = {
  Icon: MoreHoriz,
  color: 'white',
  overrides: {
    height: '50px',
    width: '50px',
    margin: '5px',
  },
};

const MobileProfile = ({ booth }: { booth: Booth }) => {
  const locale = useSelector(selectLocale);
  const { themeColor } = useSelector((s: any) => s.eventConfigs);
  const resolvedColor = themeColor || DEFAULT_THEME_COLOR;

  const [modalOpen, setModalOpen] = useState(false);

  const bannerFiles = useMemo(() => {
    const TemplatesWithLogoDesk = ['002', 'cuhk925'];
    let banners = (booth.designElements || [])
      .filter((it) => isDesignElementSupported(it, locale))
      .filter((element) => !element.name?.includes('representative'));
    const topBannerIndex = banners.findIndex((element) => (
      element.name === 'top_banner'
    ));
    const middleBannerIndex = banners.findIndex((element) => (
      element.name === 'middle_banner'
    ));
    if (TemplatesWithLogoDesk.includes(booth.appliedDesignCodename) && middleBannerIndex !== -1) {
      const [middleBanner] = banners.splice(middleBannerIndex, 1);
      banners = [{ link: booth.website, ...middleBanner } as any, ...banners];
    } else if (topBannerIndex !== -1) {
      const [topBanner] = banners.splice(topBannerIndex, 1);
      banners = [{ link: booth.website, ...topBanner } as any, ...banners];
    }
    return banners;
  }, [booth, locale]);
  const headline = useMemo(() => (
    (booth.designElements || []).find((element) => (
      element?.name === 'eye_catch_copy'
    ))?.contentRichtextHtml[locale]
  ), [booth, locale]);

  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  const SelectedLogoContainer = (
    <>
      <LogoContainer>
        <MobileBoothBannerSlider assets={bannerFiles} />
      </LogoContainer>
      <OverflowBlocker />
      <OverflowBlockerRight />
    </>
  );

  return (
    <>
      <FlexContainer>
        {SelectedLogoContainer}
        <NameText>{booth.name[locale]}</NameText>
        <HeadLine>{ReactHtmlParser(headline)}</HeadLine>
      </FlexContainer>
      {!window.location.pathname.startsWith('/preview') && (
        <PopupButton onClick={openModal} color={resolvedColor}>
          <ColoredMaterialIcon {...iconProps} />
        </PopupButton>
      )}
      <MobileProfileModal
        booth={booth}
        modalOpen={modalOpen}
        closeModal={closeModal}
      />
    </>
  );
};

export default MobileProfile;
